import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Activity, ArrowLeft, ArrowRight, LogOut, RefreshCcw, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, BODY_PARTS, CAP_LABELS, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Wordmark";
import ScoreRadar from "@/components/ScoreRadar";
import CompleteSessionButton from "@/components/CompleteSessionButton";
import DashboardExtras from "@/components/DashboardExtras";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [today, setToday] = useState(null);
  const [showPivot, setShowPivot] = useState(false);
  const [reassess, setReassess] = useState(null); // {questions, scale, idx, answers}

  const load = async () => {
    try {
      const [p, t] = await Promise.all([api.get("/me/profile"), api.get("/me/today")]);
      setProfile(p.data);
      setToday(t.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const startPivot = async (part) => {
    try {
      const { data } = await api.post("/me/pivot", { body_part: part });
      toast.success(data.message);
      setShowPivot(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const clearPivot = async () => {
    await api.delete("/me/pivot");
    toast.success("Smart Pivot cleared — full programme resumed.");
    load();
  };

  const startReassess = async () => {
    const { data } = await api.get("/me/questions");
    setReassess({ ...data, idx: 0, answers: {} });
  };

  const submitReassess = async () => {
    try {
      await api.post("/me/assessments", { answers: reassess.answers });
      toast.success("Reassessment saved — your Scorecard has been updated.");
      setReassess(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!profile || !today) {
    return (
      <div className="min-h-screen bg-ink text-white flex items-center justify-center">
        <p className="text-zinc-500 text-xs uppercase tracking-[0.25em] animate-pulse">Loading your dashboard…</p>
      </div>
    );
  }

  const plan = today.plan;
  const overallDelta = profile.history.length > 1
    ? profile.history[profile.history.length - 1].overall - profile.history[0].overall : 0;

  return (
    <div className="min-h-screen bg-ink text-white">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="flex items-center justify-between px-5 sm:px-10 h-16 border-b border-zinc-800/70 sticky top-0 bg-ink/90 backdrop-blur-md z-40">
        <Link to="/" data-testid="dash-home-link"><Wordmark className="text-2xl" /></Link>
        <div className="flex items-center gap-4">
          {user?.role === "coach" && (
            <Link to="/coach" data-testid="dash-coach-link" className="text-[10px] uppercase tracking-[0.2em] font-bold text-volt hover:text-white transition-colors">Coach</Link>
          )}
          <button data-testid="dash-logout" onClick={async () => { await logout(); navigate("/"); }}
            className="text-zinc-400 hover:text-volt transition-colors" aria-label="Sign out">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <main className="px-5 sm:px-10 lg:px-16 py-10 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
              {profile.group ? `${profile.group.name}` : "Individual programme"}
            </p>
            <h1 className="font-display uppercase text-5xl sm:text-6xl leading-[0.9]" data-testid="dash-greeting">
              {profile.user.name}
            </h1>
          </div>
          {profile.reassessment_due && profile.history.length > 0 && (
            <button data-testid="dash-reassess-due" onClick={startReassess}
              className="inline-flex items-center gap-2 border border-volt text-volt uppercase tracking-[0.16em] text-xs font-bold px-5 py-3 hover:bg-volt hover:text-black transition-colors">
              <RefreshCcw size={14} /> Monthly reassessment due
            </button>
          )}
        </div>

        {today.pivot && (
          <div data-testid="active-recovery-banner" className="mb-8 border border-volt bg-volt/10 p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Zap size={20} className="text-volt" />
              <div>
                <p className="font-display uppercase text-2xl leading-none">Active Recovery month</p>
                <p className="text-sm text-zinc-300 mt-1">
                  Smart Pivot is protecting your <span className="text-volt font-bold">{today.pivot.body_part.replace("_", " ")}</span>.
                  {today.swaps.length > 0 && ` Swapped today: ${today.swaps.map((s) => `${s.from} → ${s.to}`).join(", ")}.`}
                </p>
              </div>
            </div>
            <button data-testid="pivot-clear" onClick={clearPivot}
              className="text-xs uppercase tracking-[0.18em] font-bold text-zinc-400 hover:text-volt transition-colors">
              Feeling better? Clear pivot
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-4">
          {/* Scorecard */}
          <div className="lg:col-span-3 border border-zinc-800 bg-zinc-950 p-6 sm:p-8" data-testid="scorecard">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Capability Scorecard</p>
              {overallDelta !== 0 && (
                <span className={`font-display text-xl ${overallDelta > 0 ? "text-volt" : "text-red-400"}`}>
                  {overallDelta > 0 ? "+" : ""}{overallDelta} since baseline
                </span>
              )}
            </div>
            {profile.current ? (
              <>
                <ScoreRadar baseline={profile.baseline} current={profile.current} />
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-6">
                  {Object.entries(profile.current).map(([cap, v]) => (
                    <div key={cap} className="border border-zinc-800 p-3 text-center">
                      <p className="font-display text-2xl text-volt">{v}</p>
                      <p className="text-[9px] uppercase tracking-[0.14em] text-zinc-500 mt-1">{CAP_LABELS[cap]}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 border-t border-zinc-800 pt-4">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">History</p>
                  <div className="flex flex-wrap gap-2">
                    {profile.history.map((h, i) => (
                      <span key={i} data-testid={`history-${i}`}
                        className="text-xs border border-zinc-800 px-3 py-2 text-zinc-300">
                        {h.date} · <span className="text-volt font-bold">{h.overall}</span>
                        <span className="text-zinc-600"> · {h.type}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="py-10 text-center">
                <p className="text-zinc-400 text-sm">No assessment yet — set your baseline to unlock your scorecard.</p>
                <button data-testid="dash-first-assess" onClick={startReassess}
                  className="mt-6 bg-volt text-black uppercase tracking-[0.18em] text-xs font-extrabold px-8 py-4 hover:bg-white transition-colors">
                  Set my baseline
                </button>
              </div>
            )}
          </div>

          {/* Smart Pivot + reassess */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <div className="border border-zinc-800 bg-zinc-950 p-6 sm:p-8" data-testid="smart-pivot-card">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Motivation &amp; retention</p>
              <h2 className="font-display uppercase text-4xl mt-2 leading-none">Smart <span className="text-volt">Pivot.</span></h2>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                Something feels off? Flag it — the rest of this month switches to an Active Recovery focus and
                pain-provoking movements are automatically swapped for safer alternatives.
              </p>
              <button data-testid="smart-pivot-open" onClick={() => setShowPivot(true)}
                className="mt-6 w-full border border-volt text-volt uppercase tracking-[0.18em] text-xs font-extrabold py-4 hover:bg-volt hover:text-black transition-colors">
                Flag discomfort
              </button>
              <p className="mt-4 text-[11px] text-zinc-600 leading-relaxed">
                Sharp, radiating or worsening pain? Stop and see a qualified healthcare professional — Smart Pivot
                manages training discomfort, not injuries.
              </p>
            </div>
            <div className="border border-zinc-800 bg-zinc-950 p-6 sm:p-8" data-testid="reassess-card">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Monthly reassessment</p>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                Retake the 12-question functional check each month. Your Scorecard history tracks the trend.
              </p>
              <button data-testid="dash-reassess" onClick={startReassess}
                className="mt-5 inline-flex items-center gap-2 text-volt text-xs uppercase tracking-[0.18em] font-bold hover:text-white transition-colors">
                Reassess now <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Today's session */}
        <div className="mt-4 border border-zinc-800 bg-zinc-950" data-testid="today-session">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-800 p-6 sm:p-8">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Today's session · {today.date}</p>
              <h2 className="font-display uppercase text-4xl mt-2 leading-none">{plan.name}</h2>
              <p className="text-sm text-zinc-400 mt-2 max-w-xl">{plan.objective}</p>
            </div>
            <div className="text-right flex flex-col items-end gap-3">
              <div>
                <p className="font-display text-volt text-2xl">{plan.duration_minutes} MIN</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">RPE {plan.target_rpe}</p>
              </div>
              <CompleteSessionButton plan={plan} date={today.date} onDone={load} />
            </div>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3">
            {plan.blocks.map((b) => (
              <div key={b.key} data-testid={`plan-block-${b.key}`} className="p-6 sm:p-8 border-b xl:border-r border-zinc-800/70">
                <div className="flex items-baseline justify-between">
                  <p className="font-display uppercase text-2xl">{b.name}</p>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-volt font-bold">{b.minutes} min</span>
                </div>
                <p className="text-xs text-zinc-500 mt-1 mb-4">{b.focus}</p>
                <ul className="space-y-4">
                  {b.items.map((i) => (
                    <li key={i.movement_id} className="text-sm">
                      <div className="flex justify-between gap-3">
                        <span className="text-zinc-100 font-semibold">{i.name}</span>
                        <span className="text-zinc-500 whitespace-nowrap">{i.prescription}</span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{i.purpose}</p>
                      {(i.regression || i.progression) && (
                        <p className="text-[10px] uppercase tracking-[0.1em] text-zinc-600 mt-1">
                          {i.regression && <span>Easier: {i.regression}</span>}
                          {i.regression && i.progression && " · "}
                          {i.progression && <span>Harder: {i.progression}</span>}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <DashboardExtras profile={profile} onRefresh={load} />
      </main>

      {/* Smart Pivot modal */}
      {showPivot && (
        <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPivot(false)}>
          <div data-testid="pivot-modal" onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-zinc-950 border border-zinc-800 p-8">
            <h2 className="font-display uppercase text-4xl leading-none">Where does it <span className="text-volt">feel off?</span></h2>
            <p className="text-sm text-zinc-400 mt-3">We'll swap movements that load this area for the rest of the month.</p>
            <div className="mt-6 grid grid-cols-2 gap-2">
              {BODY_PARTS.map((p) => (
                <button key={p.id} data-testid={`pivot-part-${p.id}`} onClick={() => startPivot(p.id)}
                  className="border border-zinc-700 px-4 py-4 text-xs uppercase tracking-[0.14em] font-bold hover:bg-volt hover:text-black hover:border-volt transition-colors">
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Reassessment modal */}
      {reassess && (
        <div className="fixed inset-0 z-50 bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4">
          <div data-testid="reassess-modal" className="w-full max-w-2xl bg-zinc-950 border border-zinc-800 p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">
              <span>Reassessment — {reassess.idx + 1} of {reassess.questions.length}</span>
              <button onClick={() => setReassess(null)} data-testid="reassess-close" className="hover:text-volt transition-colors">Cancel</button>
            </div>
            <div className="h-1 bg-zinc-800 mb-8">
              <div className="h-full bg-volt transition-all" style={{ width: `${(Object.keys(reassess.answers).length / reassess.questions.length) * 100}%` }} />
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={reassess.idx} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.25 }}>
                <h3 className="font-display uppercase text-3xl sm:text-4xl leading-[0.95]" data-testid="reassess-question">
                  {reassess.questions[reassess.idx].text}
                </h3>
                <div className="mt-6 flex flex-col gap-2">
                  {reassess.scale.map((s) => (
                    <button key={s.value} data-testid={`reassess-answer-${s.value}`}
                      onClick={() => {
                        const answers = { ...reassess.answers, [reassess.questions[reassess.idx].key]: s.value };
                        if (reassess.idx < reassess.questions.length - 1) {
                          setReassess({ ...reassess, answers, idx: reassess.idx + 1 });
                        } else {
                          setReassess({ ...reassess, answers });
                          setTimeout(() => submitReassess(), 0);
                        }
                      }}
                      className="border border-zinc-700 px-5 py-4 text-left font-display uppercase text-xl tracking-wide hover:bg-volt hover:text-black hover:border-volt transition-colors">
                      {s.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      )}

      <div className="px-5 sm:px-10 lg:px-16 pb-10">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-volt transition-colors">
          <ArrowLeft size={14} /> Back to site
        </Link>
      </div>
      <Activity className="hidden" />
    </div>
  );
}
